import React, { useContext, useEffect, useState } from "react";
import {
  Checkbox,
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  SelectionMode,
  Separator,
  Stack
} from "@fluentui/react";
import {
  ISubwayNavNodeProps,
  IWizardProps,
  IWizardStepButtonProps,
  IWizardStepProps,
  LoadingState,
  SubwayNavNodeState,
  Wizard,
  WizardActionBar,
  WizardType,
  canGoToNextStep,
  canGoToPreviousStep,
  completeAllSteps,
  goToNextStep,
  goToPreviousStep,
  goToStepById,
} from "@coherence-design-system/controls";
import WizardDesign from "./WizardDesign";
import { useNavigate } from "react-router-dom";
import {
  stackTokensWithGap20,
  stackTokensWithGap30,
  stackTokensWithGap5,
} from "../Quotation/QuotationStyle";
import { QuotationContext } from "../Quotation/QuotationContext";
import {
  IBundleItemEntity,
  IResponseMessage,
  QuotationInfoType,
} from "../Quotation/Quotation.types";
import { QuotationDetailService } from "../../services/Quotations/QuotationDetailService";
import {
  IBundle,
  IBundleSlot,
  ISearchSkuResponse,
  ISlotDetail,
  IStandaloneDevice,
} from "../../common/Interfaces/ISearchSkuResponse";
import {
  IBundleProduct,
  IExtendedSlotDetail,
  IStandaloneProduct,
} from "../../common/Interfaces/IBundleProduct";
import {
  DefaultItem,
  InitialQuotationInfo,
  InitialStateOfResponseMessage,
} from "../Quotation/Common/QuotationData";
import { DeviceType } from "../../common/enum/DeviceTypes";
import { IExtendedItemEntity } from "../Quotation/Common/Interfaces/IItemEntity";
import { useBoolean } from "@fluentui/react-hooks";
import { useLocalStorageContext } from "../../Provider/LocalStorageProvider";
import { ISearchProductPayload } from "../Quotation/Components/AddItemPanelComponent/AddItemPanelComponent.types";
import { INCOMPLETE_BUNDLE_MESSAGE } from "../../Utils/Common";
import { useBundleItemSelectedKey } from "./BundleItelSelectedKeyContext";

export const DEFAULT_VALUE_OF_SELECTED_BUNDLE = {
  deviceType: "",
  id: "",
  title: "",
  bundleItems: [],
};

export const DEFAULT_VALUE_OF_SELECTED_DEVICES = {
  deviceType: "",
  id: "",
  title: "",
  standaloneItems: [],
};

export const DEFAULT_BUNDLE_DATA = {
  deviceType: "",
  bundles: [],
  standaloneDevices: [],
  standaloneMessages: [],
  bundleMessages: [],
};

export interface IBundleOptionProps {}

const BundleOption: React.FunctionComponent<IBundleOptionProps> = () => {
  const navigate = useNavigate();
  const { setUser } = useLocalStorageContext();
  const { quotationInfo, setQuotationInfo } = useContext(
    QuotationContext
  ) as QuotationInfoType;

  useEffect(()=>{
    resetBundleItemSelectedKey();
  },[])

  const { bundleItemSelectedKey,addbundleItemSelectedKey,resetBundleItemSelectedKey,replaceBundleItemSelectedKey} = useBundleItemSelectedKey();
  
  const [errorMessage, setErrorMessage] = useState<IResponseMessage>(
    InitialStateOfResponseMessage
  );
  const [hideDialog, { toggle: toggleHideDialog }] = useBoolean(true);
  const [confirmToProceed, { setTrue: setConfirmToProceed }] =
    useBoolean(false);

  const [bundleData, setBundleData] =
    useState<ISearchSkuResponse>(DEFAULT_BUNDLE_DATA);
  const [selectedBundleData, setSelectedBundleData] = useState<IBundleProduct>(
    DEFAULT_VALUE_OF_SELECTED_BUNDLE
  );
  const [selectedStandaloneData, setSelectedStandaloneData] =
    useState<IStandaloneProduct>(DEFAULT_VALUE_OF_SELECTED_DEVICES);
  const [selectedBundleItems, setSelectedBundleItems] = useState<
    ISlotDetail[] | IExtendedSlotDetail[]
  >([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [bundles, setBundles] = useState<IBundle[]>([]);
  const [itemQuantities, setItemQuantities] = useState<Map<string, number>>(new Map());
  let products: ISlotDetail[] | IExtendedSlotDetail[] = [];
  const [flag,setFlag] = useState(false);
  const getProduct = (data: ISlotDetail[] | IExtendedSlotDetail[], quantities?: Map<string, number>) => {
    products = [...data];
    if (quantities) {
      setItemQuantities(quantities);
    }
    setFlag((prevFlag) => !prevFlag);
  };

  useEffect(() => {
    setErrorMessage(() => (InitialStateOfResponseMessage));
  }, [flag]);

  const updateSelectedBundleItemsForSummary = () => {
    if (selectedBundleData.bundleItems) {
      setSelectedBundleItems([
        ...selectedBundleData.bundleItems.flatMap((p) => p),
      ]);
    }
  };
  useEffect(() => {
    updateSelectedBundleItemsForSummary();
  }, [JSON.stringify(selectedBundleData.bundleItems)]);

  useEffect(() => {
    setBundles(quotationInfo?.skuData?.bundles ?? []);
  }, []);
  const renderDialog = () => {
    return (
      <Dialog
        hidden={hideDialog}
        onDismiss={toggleHideDialog}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Confirmation",
          subText: `Your bundle is incomplete state. Do you want to complete it by selecting items from every slot as per the slot limit?`,
        }}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={() => {
              toggleHideDialog();
            }}
            text={`Yes`}
          />
          <DefaultButton
            onClick={() => {
              setConfirmToProceed && setConfirmToProceed();
            }}
            text={`No`}
          />
        </DialogFooter>
      </Dialog>
    );
  };

  useEffect(() => {
    if (confirmToProceed) {
      addItemsToCart();
    }
  }, [confirmToProceed]);

  const addItemsToCart = () => {
    const quotationInfoObj = {
      ...quotationInfo,
    };
    if (
      wizardProps.wizardComplete &&
      selectedBundleData.id &&
      selectedBundleItems
    ) {
      const bundleItems: IExtendedItemEntity[] = selectedBundleItems.map(
        (item: ISlotDetail | IExtendedSlotDetail, index: number) => {
          const { displayRank, tag, ...modifiedItem } = item;
          const itemQuantity = itemQuantities.get(item.sapItemId) || 1;
          return {
            ...DefaultItem,
            ...modifiedItem,
            key: quotationInfoObj.bundleItems.length + index,
            quantity: itemQuantity,
            unitPrice: item.msrp,
            netAmount: Number((item.msrp * itemQuantity).toFixed(2)),
            authorizedDiscount: 0.0,
            authDiscountAmounts: [],
            authDiscountPercentages: [],
            discountReason: "",
            discountedPercentage: 0,
            lineItemIndex: quotationInfoObj.bundleItems.length + index,
          };
        }
      );
      const newLineItem: IBundleItemEntity = {
        index:
          quotationInfo.bundles.length > 0
            ? quotationInfo.bundles[quotationInfo.bundles.length - 1].index + 1
            : 0,
        sapItemId: quotationInfo.searchedSku,
        bundleId: selectedBundleData.id,
        bundleTitle: selectedBundleData.title,
        startIndex: quotationInfoObj.bundles.length
          ? quotationInfoObj.bundles[quotationInfoObj.bundles.length - 1]
              .startIndex +
            quotationInfoObj.bundles[quotationInfoObj.bundles.length - 1].count
          : 0,
        count: selectedBundleItems.length,
        quantity: 1,
      };
      quotationInfoObj.bundles = [...quotationInfoObj.bundles, newLineItem];
      quotationInfoObj.bundleItems = [
        ...quotationInfoObj.bundleItems,
        ...bundleItems,
      ];
      setUser((previousUser) => ({
        ...previousUser,
        lineItemSelectedPivot: DeviceType.Bundle,
      }));
    }
    if (selectedStandaloneData.id && selectedStandaloneData.standaloneItems) {
      const newStandaloneItems: IExtendedItemEntity[] =
        selectedStandaloneData.standaloneItems.map(
          (item: IStandaloneDevice, index: number) => {
            const itemQuantity = itemQuantities.get(item.sapItemId) || 1;
            return {
              ...DefaultItem,
              ...item,
              quantity: itemQuantity,
              unitPrice: item.msrp,
              netAmount: Number((item.msrp * itemQuantity).toFixed(2)),
              authorizedDiscount: 0.0,
              authDiscountAmounts: [],
              authDiscountPercentages: [],
              discountReason: "",
              discountedPercentage: 0,
              lineItemIndex: quotationInfoObj.standaloneItems.length + index,
            };
          }
        );
      quotationInfoObj.standaloneItems = [
        ...quotationInfoObj.standaloneItems,
        ...newStandaloneItems.map(item => ({
          ...item,
          discountedPrice: 0,
          discountReason: ""
        }))
      ];
    }
    setQuotationInfo({
      ...quotationInfoObj,
      searchedSku: "",
      skuData: InitialQuotationInfo.skuData,
      bundleItems: quotationInfoObj.bundleItems,
      standaloneItems: quotationInfoObj.standaloneItems,
    });
    setWizardProps({
      steps: completeAllSteps(wizardProps.steps, true),
      wizardComplete: true,
      wizardCompleteStep: getCompletedStepToShowSummary(),
    });
    navigate(-1);
  };

  const onClickApplyButton = () => {
    if (
      wizardProps.wizardComplete &&
      selectedBundleData.id &&
      selectedBundleItems
    ) {
      addItemsToCart();
      resetBundleItemSelectedKey();
    } else if (
      selectedBundleData.id &&
      selectedBundleItems &&
      hideDialog &&
      !confirmToProceed &&
      !wizardProps.wizardComplete
    ) {
      toggleHideDialog();
    } else if (
      selectedBundleData.id === "" &&
      selectedStandaloneData.id !== ""
    ) {
      addItemsToCart();
    }
  };

  const goToApplyStepAction = (slot: IBundleSlot): IWizardStepButtonProps => {
    return {
      text: "Complete",
      onClick: () => {
        if (saveSelectedProducts(slot)) {
          setSelectedBundleItems([
            ...selectedBundleData.bundleItems.flatMap((products) => products),
          ]);
          setWizardProps({
            steps: completeAllSteps(wizardProps.steps, true),
            wizardComplete: true,
          });
        }
      },
    };
  };

  const getApplyStep = (): IWizardStepProps => {
    return {
      id: "Apply",
      label: "Apply",
      state: SubwayNavNodeState.NotStarted,
      wizardContent: {
        contentTitleElementAs: "h3",
        contentTitleElement: <>{`Bundle Summary`}</>,
        content: (
          <WizardDesign
            onSubmit={getProduct}
            items={[
              ...selectedBundleData.bundleItems.flatMap((products) => products),
            ]}
            selectedItems={[]}
            selectionMode={SelectionMode.none}
            deviceType={DeviceType.Bundle}
            initialQuantities={itemQuantities}
            onQuantityChange={(sapItemId, quantity) => {
              setItemQuantities(prev => {
                const newMap = new Map(prev);
                newMap.set(sapItemId, quantity);
                return newMap;
              });
            }}
          />
        ),
      },
      footerElement: <></>,
    };
  };

  const getCompletedStepToShowSummary = (): IWizardStepProps => {
    updateSelectedBundleItemsForSummary();
    return {
      id: "Apply",
      label: "Apply",
      state: SubwayNavNodeState.WizardComplete,
      onClick: () => {
        updateSelectedBundleItemsForSummary();
      },
      wizardContent: {
        contentTitleElementAs: "h3",
        contentTitleElement: <>{`Bundle Summary`}</>,
        content: <>Congratulation on wizard completion</>,
      },
      footerElement: <></>,
    };
  };
  const handleClickStep = (step: ISubwayNavNodeProps): void => {
    const computedSteps = goToStepById(
      wizardProps.steps,
      step.id,
      SubwayNavNodeState.Unsaved
    ).steps;

    setWizardProps({ ...wizardProps, steps: computedSteps });
  };
  const validateSelection = (slot: IBundleSlot) => {
    const { minimum, maximum } = slot;
    setErrorMessage(InitialStateOfResponseMessage);
    if (products.length < minimum) {
     setErrorMessage(() => ({
       showMessage: true,
       message: `Please select at least ${minimum} items form current slot.`,
    }));
      return false;
    } else if (products.length > maximum) {  
      setErrorMessage(()=>({
        showMessage: true,
        message: `You can't select more than ${maximum} items form current slot.`,
      }));
      return false;
    } else if (products.length > 0) {
      return true;
    }
  };
  const saveSelectedProducts = (slot: IBundleSlot): boolean => { 
    const { slotId } = slot;
    if (products.length === 0 && slot.minimum === 0) {
      return true;
    } else if (validateSelection(slot)) {
      const currentSelectedKey = products.map(item=>{
        const currentItems = item as IExtendedSlotDetail;
        return currentItems.key.toString();
      })
    
      Array.from({ length: slotId - bundleItemSelectedKey.length + 1 }).forEach(() => {
        addbundleItemSelectedKey([]);
      });

     replaceBundleItemSelectedKey(slotId,currentSelectedKey);

      setSelectedBundleData((prevSelectedProducts: IBundleProduct) => {
        // Return the new state for selectedProducts
        return {
          ...prevSelectedProducts,
          bundleItems: [
            ...prevSelectedProducts.bundleItems.map((item, index) =>
              index === slotId ? [...products] : item
            ),
          ],
        };
      });
      if (slotId < bundleData?.bundles[0].bundleSlots?.length! - 1) {
        setSelectedBundleItems((previousSelectedBundleItem) => {
          return [...previousSelectedBundleItem, ...products];
        });
      }
      products = [];
      return true;
    } else {
      return false;
    }
  };
  const goToNextStepAction = (slot: IBundleSlot): IWizardStepButtonProps => {
    return {
      text: "Next",
      onClick: () => {
        if (saveSelectedProducts(slot)) {
          setWizardProps((wizardProps) => {
            if (
              canGoToNextStep(wizardProps.steps)
            ) {
              const { steps, newCurrentStep } = goToNextStep(
                wizardProps.steps,
                SubwayNavNodeState.ViewedNotCompleted
              );

              if (steps) {
                setWizardProps({
                  ...wizardProps,
                  steps: [
                    ...steps.map((step) =>
                      Number(step.id) === slot.slotId
                        ? {
                            ...step,
                            state:
                              slot.slotId ===
                              Number(
                                wizardProps.steps[wizardProps.steps.length - 1]
                                  .id
                              )
                                ? SubwayNavNodeState.WizardComplete
                                : SubwayNavNodeState.Completed,
                          }
                        : step
                    ),
                  ],
                });
              } else {
              }
            }
            return wizardProps;
          });
        }
        console.log("SLOT IN FUNCTION", slot);
      },
    };
  };
  const goToPrevStepAction = (slot: IBundleSlot): IWizardStepButtonProps => {
    return {
      text: "Back",
      onClick: () => {
        console.log(
          "PRODUCT",
          products,
          slot,
          selectedBundleData.bundleItems.find(
            (product, index) => index === slot.slotId - 1
          )
        );
        products = [];
        setWizardProps((wizardProps) => {
          if (canGoToPreviousStep(wizardProps.steps)) {
            const { steps, newCurrentStep } = goToPreviousStep(
              wizardProps.steps,
              SubwayNavNodeState.NotStarted
            );

            if (steps) {
              console.log("Now at step: " + newCurrentStep!.label);
              setWizardProps({
                ...wizardProps,
                steps: [
                  ...steps.map((step) =>
                    step.id === newCurrentStep!.id
                      ? {
                          ...step,
                          state: SubwayNavNodeState.Current,
                        }
                      : Number(step.id) > Number(newCurrentStep!.id)
                      ? {
                          ...step,
                          state: SubwayNavNodeState.NotStarted,
                        }
                      : step
                  ),
                ],
              });
              return { ...wizardProps, steps };
            } else {
              console.log(
                "Oops, something happened when trying to go to the next steps"
              );
            }
          }
          return wizardProps; // No state update
        });
      },
    };
  };

  const getTestSteps = (): IWizardStepProps[] => {
    const lastFooter = (slot: IBundleSlot) => {
      return (
        <WizardActionBar
          backAction={goToPrevStepAction(slot)}
          // mainAction={goToNextStepAction(slot)}
          // mainAction={goToCompleteStepAction(slot)}
          mainAction={goToApplyStepAction(slot)}
          // cancelAction={this._goToCancelStepAction()}
          currentStep={{} as IWizardStepProps}
        />
      );
    };

    const inBetweenFooter = (slot: IBundleSlot) => {
      return (
        <WizardActionBar
          backAction={goToPrevStepAction(slot)}
          mainAction={goToNextStepAction(slot)}
          currentStep={{} as IWizardStepProps}
        />
      );
    };
    if (
      bundleData?.deviceType === DeviceType.Bundle &&
      bundleData?.bundles[0]?.bundleSlots &&
      bundleData?.bundles[0]?.bundleSlots.length > 0
    ) {
      const { bundleSlots } = bundleData.bundles[0];
      const stepsData: IWizardStepProps[] =
        bundleSlots?.map((slot: IBundleSlot, index: number) => {
          const value: IWizardStepProps = {
            id: slot.slotId.toString(),
            label: `Slot ${slot.slotId}*`,
             state:
              index === 0
                ? SubwayNavNodeState.Current
                : SubwayNavNodeState.NotStarted,
            footerElement:
              index < bundleSlots.length - 1
                ? inBetweenFooter(slot)
                : lastFooter(slot),
            announcedProps: { message: `Step ${slot.slotId}` },
            wizardContent: {
              contentTitleElementAs: "h3",
              contentTitleElement: (
                <>
                  {slot.minimum > 0
                    ? slot.maximum === 1
                      ? `Slot ${slot.slotId} (Required) Select ${slot.maximum}`
                      : `Slot ${slot.slotId} (Required) Select up to ${slot.maximum}`
                    : slot.maximum === 1
                    ? `Slot ${slot.slotId} (Optional) Select ${slot.maximum}`
                    : `Slot ${slot.slotId} (Optional) Select up to ${slot.maximum}`}
                </>
              ),
              content: (
                <>
                  <WizardDesign
                    onSubmit={getProduct}
                    items={[
                      ...slot.slotDetails.map(
                        (item: ISlotDetail, index: number) => ({
                          key: `${slot.slotId}-${index}`,
                          ...item,
                        })
                      ),
                    ]}
                    selectedItems={selectedBundleData.bundleItems[slot.slotId]}
                    selectionMode={SelectionMode.multiple}
                    deviceType={bundleData.deviceType}
                    initialQuantities={itemQuantities}
                    onQuantityChange={(sapItemId, quantity) => {
                      setItemQuantities(prev => {
                        const newMap = new Map(prev);
                        newMap.set(sapItemId, quantity);
                        return newMap;
                      });
                    }}
                  />
                </>
              ),
            },
          };
          return value;
        }) ?? "No data";
      return stepsData;
    } else {
      return [
        {
          id: `0`,
          label: `No Slot exist`,
          state: SubwayNavNodeState.Error,
          footerElement: <></>,
          announcedProps: { message: `Step 0` },
          wizardContent: {
            contentTitleElementAs: "h3",
            contentTitleElement: <></>,
            content: <h3>{`Selected bundle does not have any data`}</h3>,
          },
        },
      ];
    }
  };

  useEffect(() => {
    if (
      (quotationInfo?.skuData?.deviceType === DeviceType.Bundle ||
        quotationInfo?.skuData?.deviceType === DeviceType.Both) &&
      selectedBundleData.id
    ) {
      getSelectedBundleDetails();
    }
  }, [
    selectedBundleData.id,
    quotationInfo.searchedSku,
    quotationInfo.customer.customerMarket,
  ]);

  const getSelectedBundleDetails = async () => {
    setIsLoading(true);
    try {
      const searchBundlePayload: ISearchProductPayload = {
        sapId: quotationInfo.searchedSku,
        bigId: selectedBundleData.id,
        market: quotationInfo?.customer?.customerMarket ?? "US",
        languageCode: quotationInfo?.customer?.languageCode ?? "en-US",
        customerId: quotationInfo?.customer?.id ?? ""
      };
      const res = await QuotationDetailService.getDetailsOfSelectedBundle(
        searchBundlePayload
      );

      if (res.data.code === 400) {
        const updatedBundles = [...bundles];
        const selectedBundleIndex = updatedBundles.findIndex(
          (bundle) => bundle.bundleId === selectedBundleData.id
        );
        updatedBundles.splice(selectedBundleIndex, 1, {
          ...updatedBundles[selectedBundleIndex],
          message: res.data.message,
        });
        setBundles(updatedBundles);
      } else if (
        res?.data?.data?.deviceType === DeviceType.Bundle ||
        res?.data?.data?.deviceType === DeviceType.Both
      ) {
       
        const {
          deviceType,
          bundles,
          standaloneDevices,
          standaloneMessages,
          bundleMessages,
        } = res?.data?.data;
        setBundleData({
          deviceType: deviceType ?? "",
          bundles: bundles ?? [],
          standaloneDevices: standaloneDevices ?? [],
          standaloneMessages: standaloneMessages ?? [],
          bundleMessages: bundleMessages ?? [],
        });
         
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const DEFAULT_WIZARD_PROPS = {
    steps: getTestSteps(),
    wizardComplete: false,
  };
  const [wizardProps, setWizardProps] =
    useState<IWizardProps>(DEFAULT_WIZARD_PROPS);

  useEffect(() => {
    if (wizardProps?.wizardComplete) {
      const flatData = selectedBundleData.bundleItems.flatMap((p) => p);
      setSelectedBundleItems(flatData);
    }
  }, [wizardProps?.wizardComplete]);
  useEffect(() => {
    const wizardObj = {
      steps: getTestSteps(),
    };
    selectedBundleData.bundleItems.length &&
    wizardObj.steps.length > 0 &&
    wizardObj.steps[0].state === SubwayNavNodeState.Error
      ? setWizardProps(wizardObj)
      : setWizardProps({
          ...wizardObj,
          wizardComplete: false,
          wizardCompleteStep: getCompletedStepToShowSummary(),
        });
    selectedBundleData.id &&
      wizardObj.steps.length > 0 &&
      wizardObj.steps[0].state !== SubwayNavNodeState.Error &&
      setSelectedBundleData({
       ...selectedBundleData,
        bundleItems: [
          ...Array.from({ length: wizardObj.steps.length }, () => []),
        ],
      });
  }, [JSON.stringify(bundleData)]);

  const renderSelectedBundle = () => {
    return (
      <Stack style={{ marginTop: 50 }}>
        <Wizard
          type={WizardType.FULL_PAGE}
          wizardProps={{
            ...wizardProps,
          }}
          styles={{
            root: {
              "& > div > div:nth-child(2) > div:nth-child(2)": {
                maxWidth: "none",
              },
            },
          }}
        />
      </Stack>
    );
  };

  const renderSelectedDevices = () => {
    return (
      <Stack style={{ marginTop: 50 }}>
        <WizardDesign
          onSubmit={getProduct}
          items={selectedStandaloneData.standaloneItems}
          selectedItems={[]}
          selectionMode={SelectionMode.none}
          deviceType={DeviceType.Device}
          initialQuantities={itemQuantities}
          onQuantityChange={(sapItemId, quantity) => {
            setItemQuantities(prev => {
              const newMap = new Map(prev);
              newMap.set(sapItemId, quantity);
              return newMap;
            });
          }}
        />
      </Stack>
    );
  };

  const renderSelectedBundleItems = () => {
    return (
      <Stack style={{ marginTop: 50 }}>
        <WizardDesign
          onSubmit={getProduct}
          items={
            selectedBundleItems.length > 0
              ? selectedBundleItems
              : [
                  ...selectedBundleData.bundleItems.flatMap(
                    (products) => products
                  ),
                ]
          }
          selectedItems={[]}
          selectionMode={SelectionMode.none}
          deviceType={DeviceType.Bundle}
          initialQuantities={itemQuantities}
          onQuantityChange={(sapItemId, quantity) => {
            setItemQuantities(prev => {
              const newMap = new Map(prev);
              newMap.set(sapItemId, quantity);
              return newMap;
            });
          }}
        />
      </Stack>
    );
  };

  return (
    <Stack>
      <Stack horizontal horizontalAlign="center">
        <Stack
          horizontal
          horizontalAlign="center"
          tokens={stackTokensWithGap20}
        >
          <Separator vertical />
          <Stack>
            <h3>Select Bundle</h3>
            <Stack horizontalAlign="center">
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  width: "400px",
                }}
              >
                {bundles?.length > 0 ? (
                  bundles.map((item: IBundle, key: number) => {
                    return (
                      <Stack
                        key={key}
                        verticalFill
                        tokens={stackTokensWithGap5}
                        style={{ height: "auto" }}
                      >
                        <Stack
                          key={key}
                          style={{
                            border:
                              selectedBundleData.id === item.bundleId
                                ? "3px solid blue"
                                : "3px solid #808080",
                            padding: "5px",
                            height: "120px",
                            width: "120px",
                            margin: "5px",
                            position: "relative",
                            boxSizing: "border-box",
                            overflow: "hidden", 
                          }}
                        >
                          <Stack
                            tabIndex={0}
                            role="region"
                            aria-label={item.bundleTitle}
                            style={{
                              height: "110px",
                              width: "110px",
                              overflow: "auto",  
                              paddingRight: "10px",
                              boxSizing: "border-box",
                              textAlign: "center",
                              justifyContent: "center",
                            }}
                          >
                            <p style={{ margin: "0 5 0 0" }}>
                              {item.bundleTitle}
                            </p>
                          </Stack>
                          <Checkbox
                            styles={{
                              root: {
                                position: "absolute",
                                top: 4,
                                right: 2,
                                padding: 0,
                                margin: 0,
                              },
                            }}
                            ariaLabel={item.bundleTitle}
                            id={item.bundleId}
                            checked={selectedBundleData.id === item.bundleId}
                            disabled={
                              item.message === INCOMPLETE_BUNDLE_MESSAGE
                            }
                            onChange={(
                              ev?: React.FormEvent<
                                HTMLElement | HTMLInputElement
                              >,
                              isChecked?: boolean
                            ) => {
                              setIsLoading(false);
                              if (
                                isChecked &&
                                item.bundleId !== selectedBundleData.id
                              ) {
                                setIsLoading(true);
                                setBundleData(DEFAULT_BUNDLE_DATA);
                                setSelectedBundleItems([]);
                                setWizardProps(DEFAULT_WIZARD_PROPS);
                                setErrorMessage(InitialStateOfResponseMessage);
                                setSelectedBundleData({
                                  ...selectedBundleData,
                                  deviceType:
                                    quotationInfo?.skuData?.deviceType,
                                  id: item.bundleId,
                                  title: item.bundleTitle,
                                  bundleItems: [],
                                });
                                resetBundleItemSelectedKey();
                              } else {
                                setErrorMessage(InitialStateOfResponseMessage);
                                setSelectedBundleData(
                                  DEFAULT_VALUE_OF_SELECTED_BUNDLE
                                );
                                setWizardProps(DEFAULT_WIZARD_PROPS);
                                setSelectedBundleItems([]);
                                resetBundleItemSelectedKey();
                              }
                              products = [];
                            }}
                          />
                        </Stack>
                        <p
                          style={{
                            width: "100px",
                            margin: "0px 0px 0px 10px",
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            color: "rgb(0, 120, 212)",
                            fontSize: "12px",
                          }}
                        >
                          {item.message}
                        </p>
                      </Stack>
                    );
                  })
                ) : (
                  <p>No bundle products for searched SKU</p>
                )}
              </div>
            </Stack>
          </Stack>
          <Separator vertical />
          <Stack>
            <h3>Select Non-Bundle</h3>
            <Stack horizontalAlign="center">
              <div
                style={{
                  // border: '1px solid black',
                  display: "flex",
                  flexWrap: "wrap",
                  width: "400px",
                }}
              >
                {quotationInfo?.skuData?.standaloneDevices?.length > 0 ? (
                  quotationInfo.skuData.standaloneDevices?.map(
                    (item: IStandaloneDevice, key: number) => {
                      const { skuId, productId, isPreOrder, msrp } = item;
                      const uniqueId = `${skuId}_${productId}_${isPreOrder}_${msrp}`;
                      return (
                        <Stack
                          key={key}
                          style={{
                            border:
                              selectedStandaloneData.id === uniqueId
                                ? "3px solid blue"
                                : "3px solid #808080",
                            padding: "5px",
                            height: "120px",
                            width: "120px",
                            margin: "5px",
                            position: "relative",
                            boxSizing: "border-box",
                            overflow: "hidden", 
                          }}
                        >
                          <Stack
                            tabIndex={0}
                            role="region"
                            aria-label={item.productTitle}
                            style={{
                              height: "110px",
                              width: "110px",
                              overflow: "auto",
                              paddingRight: "25px",
                              boxSizing: "border-box",
                              textAlign: "start",
                            }}
                          >
                            <p style={{ margin: "0 5 0 0" }}>
                              {item.productTitle}
                            </p>
                          </Stack>
                          <Checkbox
                            styles={{
                              root: {
                                position: "absolute",
                                top: 4,
                                right: 12,
                                padding: 0,
                                margin: 0,
                              },
                            }}
                            ariaLabel={item.productTitle}
                            id={uniqueId}
                            checked={selectedStandaloneData.id === uniqueId}
                            onChange={(
                              ev?: React.FormEvent<
                                HTMLElement | HTMLInputElement
                              >,
                              isChecked?: boolean
                            ) => {
                              if (
                                isChecked
                              ) {
                                setErrorMessage(InitialStateOfResponseMessage);
                                setSelectedStandaloneData({
                                  deviceType:
                                    quotationInfo?.skuData?.deviceType,
                                  id: uniqueId,
                                  title: item.productTitle,
                                  standaloneItems: [item],
                                });
                                // }
                              } else {
                                setErrorMessage(InitialStateOfResponseMessage);
                                setSelectedStandaloneData(
                                  DEFAULT_VALUE_OF_SELECTED_DEVICES
                                );
                              }
                              products = [];
                            }}
                          />
                        </Stack>
                      );
                    }
                  )
                ) : (
                  <p>No standalone devices for searched SKU</p>
                )}
              </div>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      {(selectedBundleData.bundleItems.length === 0 ||
        wizardProps?.wizardComplete) && <Separator />}
      <Stack>
        {errorMessage.showMessage && (
          <MessageBar
            messageBarType={MessageBarType.error}
            onDismiss={() => setErrorMessage(InitialStateOfResponseMessage)}
            dismissButtonAriaLabel="Dismiss error message"
          >
            {errorMessage.message}
          </MessageBar>
        )}
      </Stack>
      {isLoading ? (
        <Stack style={{ marginTop: "10%" }}>
          <LoadingState primaryText="Loading your content..." />
        </Stack>
      ) : selectedBundleData.id &&
        bundleData &&
        wizardProps?.steps &&
        !wizardProps.wizardComplete ? (
        renderSelectedBundle()
      ) : wizardProps.wizardComplete ? (
        renderSelectedBundleItems()
      ) : null}
      {selectedStandaloneData.id &&
        
        renderSelectedDevices()}
      {!hideDialog && renderDialog()}
      <Stack horizontal horizontalAlign="center" tokens={stackTokensWithGap30}>
        {!isLoading && (
          <PrimaryButton
            disabled={!hideDialog}
            onClick={() => {
              setQuotationInfo({
                ...quotationInfo,
                skuData: InitialQuotationInfo.skuData,
                searchedSku: InitialQuotationInfo.searchedSku,
              });
              navigate(-1);
            }}
          >
            Back to Line Items
          </PrimaryButton>
        )}
        {((selectedBundleData.bundleItems.length > 0 &&
          selectedBundleItems.length > 0 &&
          wizardProps.wizardComplete) ||
          selectedStandaloneData.standaloneItems.length > 0) && (
          <PrimaryButton disabled={!hideDialog} onClick={onClickApplyButton}>
            Apply
          </PrimaryButton>
        )}
      </Stack>
    </Stack>
  );
};

export default BundleOption;
